import type { SelfServiceRequest } from "../types";

export interface StatusGroup {
  labelKey: string;
  codes: string[];
}

export const ACTIVE_STATUS_GROUPS: StatusGroup[] = [
  { labelKey: "requests.status.draft", codes: ["draft"] },
  { labelKey: "requests.status.revision", codes: ["revision"] },
  { labelKey: "requests.status.pending", codes: ["signing_pending", "pending", "agr_pending", "pending_review", "pending_signing"] },
  { labelKey: "requests.status.approved", codes: ["app_approved", "approved"] },
  { labelKey: "requests.status.orderPending", codes: ["order_pending"] },
  { labelKey: "requests.status.orderUploaded", codes: ["order_uploaded", "order_acknowledged"] },
  { labelKey: "requests.status.fulfilling", codes: ["fulfilling"] },
  { labelKey: "requests.status.active", codes: ["active"] },
  { labelKey: "requests.status.issued", codes: ["issued"] },
  { labelKey: "requests.status.pendingHr", codes: ["pending_hr"] },
  { labelKey: "requests.status.hrApproved", codes: ["hr_approved"] },
];

export const HISTORY_STATUS_GROUPS: StatusGroup[] = [
  { labelKey: "requests.status.completed", codes: ["completed"] },
  { labelKey: "requests.status.settled", codes: ["settled"] },
  { labelKey: "requests.status.cancelled", codes: ["cancelled", "revoked"] },
  { labelKey: "requests.status.fulfilled", codes: ["fulfilled"] },
  { labelKey: "requests.status.rejected", codes: ["rejected", "hr_rejected", "chief_rejected"] },
  { labelKey: "requests.status.applied", codes: ["applied"] },
  { labelKey: "requests.status.signed", codes: ["signed", "agreement_signed"] },
  { labelKey: "requests.status.paid", codes: ["paid"] },
];

export const ACTIVE_REQUEST_STATUSES = [
  "draft",
  "revision",
  "signing_pending",
  "app_approved",
  "order_pending",
  "order_uploaded",
  "order_acknowledged",
  "pending",
  "approved",
  "fulfilling",
  "active",
  "issued",
  "agr_pending",
  "pending_review",
  "pending_signing",
  "pending_hr",
  "hr_approved",
] as const;

export const HISTORY_REQUEST_STATUSES = [
  "completed",
  "settled",
  "cancelled",
  "fulfilled",
  "rejected",
  "applied",
  "signed",
  "paid",
  "agreement_signed",
  "revoked",
  "hr_rejected",
  "chief_rejected",
] as const;

export type RequestView = "all" | "active" | "history";

export function filterRequestsByView<T extends { status: string }>(
  requests: T[] | undefined,
  view: RequestView,
): T[] | undefined {
  if (!requests) return undefined;
  if (view === "all") return requests;
  if (view === "active")
    return requests.filter((r) =>
      ACTIVE_REQUEST_STATUSES.includes(r.status as (typeof ACTIVE_REQUEST_STATUSES)[number]),
    );
  if (view === "history")
    return requests.filter((r) =>
      HISTORY_REQUEST_STATUSES.includes(r.status as (typeof HISTORY_REQUEST_STATUSES)[number]),
    );
  return requests;
}

function isLeaveType(type: string): boolean {
  return type === "annual_leave" || type === "unpaid_leave" || type === "medical_leave";
}

export function isRevisionRequested(request: SelfServiceRequest | null): boolean {
  if (!request) return false;
  if (request.status === "revision") return true;
  return (
    request.status === "draft" &&
    request.application_review_note !== null &&
    request.application_review_note !== ""
  );
}

export function canEditLeaveRequest(request: SelfServiceRequest | null): boolean {
  if (!request) return false;
  return request.status === "draft" && isLeaveType(request.type);
}

export function canEditAmendmentRequest(request: SelfServiceRequest | null): boolean {
  if (!request) return false;
  return request.status === "draft" && request.type === "amendment";
}

export function canEditResignationRequest(request: SelfServiceRequest | null): boolean {
  if (!request) return false;
  return request.status === "draft" && request.type === "resignation";
}

export function canEditRequest(request: SelfServiceRequest | null): boolean {
  return canEditLeaveRequest(request) || canEditAmendmentRequest(request) || canEditResignationRequest(request);
}

export function canDeleteRequest(request: SelfServiceRequest | null): boolean {
  return canEditRequest(request);
}

export function canDeleteLeaveRequest(request: SelfServiceRequest | null): boolean {
  if (!request) return false;
  return isLeaveType(request.type) && (request.status === "draft" || request.status === "pending_review");
}

export function canDeleteAdvanceRequest(request: SelfServiceRequest | null): boolean {
  if (!request) return false;
  return request.type === "advance" && (request.status === "draft" || request.status === "signing_pending");
}

export function canEditAdvanceRequest(request: SelfServiceRequest | null): boolean {
  if (!request) return false;
  return request.type === "advance" && request.status === "revision" && request.details?.kind === "employee";
}

export function canAcknowledgeOrder(request: SelfServiceRequest | null): boolean {
  if (!request) return false;
  const isResignationType = request.type === "resignation";
  return request.status === "order_uploaded" && (isLeaveType(request.type) || isResignationType);
}

